import {
  Activity,
  AlertTriangle,
  ArrowRight,
  BarChart3,
  CalendarDays,
  Clock3,
  CreditCard,
  FileSignature,
  FileText,
  LayoutDashboard,
  Search,
  SlidersHorizontal,
  TrendingDown,
  Users,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { getAdminJourneyDashboard } from "../../lib/api";
import type { JourneySummary } from "../../lib/types";
import { FormPortalHeader } from "./FormPortalHeader";

const pipelineStages = [
  "New Inquiry",
  "Discovery Email Sent",
  "Discovery Scheduled",
  "Discovery Completed",
  "Proposal Approved to Send",
  "Proposal Sent",
  "Proposal Viewed",
  "Proposal Accepted",
  "Proposal Revision Requested",
  "Agreement Sent",
  "Agreement Viewed",
  "Agreement Signed",
  "Invoice Created",
  "Invoice Sent",
  "Payment Pending",
  "Partial Payment Received",
  "Awaiting Final Payment",
  "Paid In Full",
  "Welcome Sent",
  "Onboarding Sent",
  "Onboarding Complete",
  "Project Active",
  "Build In Progress",
  "Internal Review",
  "Client Review",
  "Revision Requested",
  "Final Approval",
  "Project Complete",
  "Delivered",
  "Follow-Up Sent",
  "Testimonial Requested",
  "Referral Invitation Sent",
  "Archived",
] as const;

const funnelStages = [
  { label: "Inquiry Submitted / New Inquiry", statuses: ["Inquiry Submitted", "New Inquiry"] },
  { label: "Discovery Completed", statuses: ["Discovery Completed"] },
  { label: "Proposal Sent", statuses: ["Proposal Sent"] },
  { label: "Proposal Accepted", statuses: ["Proposal Accepted"] },
  { label: "Agreement Signed", statuses: ["Agreement Signed"] },
  { label: "Paid In Full", statuses: ["Paid In Full"] },
] as const;

const completedStatuses = new Set([
  "Paid In Full",
  "Project Complete",
  "Delivered",
  "Follow-Up Sent",
  "Testimonial Requested",
  "Referral Invitation Sent",
  "Archived",
]);

const stuckThresholdDays: Record<string, number> = {
  "New Inquiry": 2,
  "Discovery Email Sent": 2,
  "Discovery Scheduled": 7,
  "Discovery Completed": 4,
  "Proposal Approved to Send": 2,
  "Proposal Sent": 5,
  "Proposal Viewed": 5,
  "Proposal Accepted": 4,
  "Proposal Revision Requested": 5,
  "Agreement Sent": 4,
  "Agreement Viewed": 4,
  "Agreement Signed": 4,
  "Payment Pending": 4,
  "Invoice Created": 3,
  "Invoice Sent": 5,
  "Partial Payment Received": 14,
  "Awaiting Final Payment": 14,
  "Paid In Full": 3,
  "Welcome Sent": 3,
  "Onboarding Sent": 5,
  "Onboarding Complete": 4,
  "Project Active": 7,
  "Build In Progress": 10,
  "Internal Review": 5,
  "Client Review": 7,
  "Revision Requested": 7,
  "Final Approval": 5,
  "Project Complete": 3,
  Delivered: 7,
  "Follow-Up Sent": 14,
  "Testimonial Requested": 21,
  "Referral Invitation Sent": 21,
  Archived: 0,
  "Awaiting Client Content": 7,
  "On Hold": 14,
};

type DashboardFilters = {
  query: string;
  dateFrom: string;
  dateTo: string;
  leadSource: string;
  serviceInterest: string;
  eventType: string;
  paymentStatus: string;
};

export function ClientJourneyDashboardPage() {
  const [journeys, setJourneys] = useState<JourneySummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState<DashboardFilters>({
    query: "",
    dateFrom: "",
    dateTo: "",
    leadSource: "All",
    serviceInterest: "All",
    eventType: "All",
    paymentStatus: "All",
  });

  useEffect(() => {
    document.title = "Sales Dashboard | Kreatethedate";
    void getAdminJourneyDashboard()
      .then((data) => setJourneys(data.journeys || []))
      .catch((loadError) => setError(loadError instanceof Error ? loadError.message : "Unable to load journey dashboard."))
      .finally(() => setLoading(false));
  }, []);

  const filteredJourneys = useMemo(() => {
    const q = filters.query.trim().toLowerCase();
    const from = filters.dateFrom ? new Date(`${filters.dateFrom}T00:00:00`) : null;
    const to = filters.dateTo ? new Date(`${filters.dateTo}T23:59:59.999`) : null;

    return journeys.filter((journey) => {
      const leadCreatedAt = new Date(journey.leadCreatedAt);
      if (from && !(leadCreatedAt >= from)) return false;
      if (to && !(leadCreatedAt <= to)) return false;
      if (filters.leadSource !== "All" && journey.leadSource !== filters.leadSource) return false;
      if (filters.serviceInterest !== "All" && !includesText(journey.serviceInterest, filters.serviceInterest)) return false;
      if (filters.eventType !== "All" && journey.eventType !== filters.eventType) return false;
      if (filters.paymentStatus !== "All" && journey.paymentStatus !== filters.paymentStatus) return false;

      if (!q) return true;
      return [
        journey.leadName,
        journey.leadEmail,
        journey.leadSource,
        journey.serviceInterest,
        journey.eventType,
        journey.eventDate,
        journey.currentStatus,
        journey.proposalStatus,
        journey.agreementStatus,
        journey.paymentStatus,
        journey.projectStatus,
      ]
        .join(" ")
        .toLowerCase()
        .includes(q);
    });
  }, [journeys, filters]);

  const lookupSets = useMemo(() => {
    return {
      leadSources: uniqueSorted(journeys.map((journey) => journey.leadSource).filter(Boolean)),
      serviceInterests: uniqueSorted(splitValues(journeys.map((journey) => journey.serviceInterest))),
      eventTypes: uniqueSorted(journeys.map((journey) => journey.eventType).filter(Boolean)),
      paymentStatuses: uniqueSorted(journeys.map((journey) => journey.paymentStatus).filter(Boolean)),
    };
  }, [journeys]);

  const analytics = useMemo(() => buildAnalytics(filteredJourneys), [filteredJourneys]);

  const headlineMetrics = [
    { icon: Users, label: "Total Leads", value: filteredJourneys.length },
    { icon: FileText, label: "Proposal Stage", value: filteredJourneys.filter((journey) => journey.proposalStatus !== "Not Started").length },
    { icon: FileSignature, label: "Agreement Stage", value: filteredJourneys.filter((journey) => journey.agreementStatus !== "Not Started").length },
    { icon: CreditCard, label: "Payment Stage", value: filteredJourneys.filter((journey) => journey.paymentStatus !== "Not Started").length },
  ];

  const attentionLeads = [...filteredJourneys]
    .filter((journey) => !completedStatuses.has(canonicalStatus(journey.currentStatus)))
    .sort((a, b) => new Date(b.leadCreatedAt).getTime() - new Date(a.leadCreatedAt).getTime())
    .slice(0, 6);

  const stuckLeads = [...filteredJourneys]
    .map((journey) => {
      const currentStatus = canonicalStatus(journey.currentStatus);
      const daysInStage = daysSince(statusStartedAt(journey, currentStatus));
      const threshold = stuckThresholdDays[currentStatus] ?? 7;
      return { journey, currentStatus, daysInStage, threshold };
    })
    .filter((item) => item.threshold > 0 && item.daysInStage > item.threshold)
    .sort((a, b) => b.daysInStage - a.daysInStage)
    .slice(0, 6);

  return (
    <main className="command-center-shell min-h-screen text-[#fff9ef]">
      <FormPortalHeader />
      <section className="relative isolate overflow-hidden border-b border-[#D94A16]/18 bg-[#050505] px-4 py-12 text-[#fff9ef] sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,106,43,.18),transparent_34%),linear-gradient(120deg,rgba(5,5,5,.96),rgba(22,18,10,.9))]" />
        <div className="relative mx-auto grid max-w-7xl gap-5">
          <div className="inline-flex w-fit items-center gap-2 rounded-full border border-[#FF6A2B]/35 px-3 py-2 text-xs font-black uppercase tracking-[0.2em] text-[#FF6A2B]">
            <LayoutDashboard className="size-4" />
            Sales Dashboard
          </div>
          <h1 className="max-w-4xl font-display text-5xl font-bold leading-tight sm:text-6xl">Sales Pipeline / Prospect Journey</h1>
          <p className="max-w-3xl text-base font-semibold leading-8 text-[#fff9ef]/76">
            Track each prospect from inquiry through payment, delivery, testimonial follow-up, and archiving inside the
            Forms Portal only.
          </p>
        </div>
      </section>

      <section className="px-4 py-10 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-8">
          <div className="grid gap-4 md:grid-cols-4">
            {headlineMetrics.map((metric) => (
              <MetricCard key={metric.label} icon={metric.icon} label={metric.label} value={metric.value} />
            ))}
          </div>

          <section className="command-center-card rounded-lg p-5 sm:p-6">
            <div className="flex flex-col gap-4 border-b border-[#D94A16]/16 pb-5 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.18em] text-[#FF6A2B]">Forms Portal Admin</p>
                <h2 className="mt-1 font-display text-3xl font-bold text-[#fff9ef]">Sales Pipeline Overview</h2>
                <p className="mt-2 max-w-3xl text-sm font-semibold leading-7 text-[#fff9ef]/72">
                  Funnel, stage counts, lead-source trends, average time in stage, and action lists for prospects that
                  need attention.
                </p>
              </div>
              <label className="relative block lg:w-[340px]">
                <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#FF6A2B]" aria-hidden="true" />
                <input
                  value={filters.query}
                  onChange={(event) => setFilters((current) => ({ ...current, query: event.target.value }))}
                  className="command-center-field focus-ring min-h-12 w-full rounded-lg pl-11 pr-4 text-sm font-semibold"
                  placeholder="Search by name, email, status, source, or event"
                />
              </label>
            </div>

            <div className="mt-5 grid gap-4 lg:grid-cols-5">
              <FilterField label="Date From">
                <input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(event) => setFilters((current) => ({ ...current, dateFrom: event.target.value }))}
                    className="command-center-field focus-ring min-h-11 w-full rounded-lg px-3 text-sm font-semibold"
                />
              </FilterField>
              <FilterField label="Date To">
                <input
                  type="date"
                  value={filters.dateTo}
                  onChange={(event) => setFilters((current) => ({ ...current, dateTo: event.target.value }))}
                    className="command-center-field focus-ring min-h-11 w-full rounded-lg px-3 text-sm font-semibold"
                />
              </FilterField>
              <FilterField label="Lead Source">
                <SelectField
                  value={filters.leadSource}
                  onChange={(value) => setFilters((current) => ({ ...current, leadSource: value }))}
                  options={["All", ...lookupSets.leadSources]}
                />
              </FilterField>
              <FilterField label="Service Interest">
                <SelectField
                  value={filters.serviceInterest}
                  onChange={(value) => setFilters((current) => ({ ...current, serviceInterest: value }))}
                  options={["All", ...lookupSets.serviceInterests]}
                />
              </FilterField>
              <FilterField label="Event Type">
                <SelectField
                  value={filters.eventType}
                  onChange={(value) => setFilters((current) => ({ ...current, eventType: value }))}
                  options={["All", ...lookupSets.eventTypes]}
                />
              </FilterField>
            </div>
            <div className="mt-4 grid gap-4 lg:grid-cols-[1fr_240px]">
              <FilterField label="Payment Status">
                <SelectField
                  value={filters.paymentStatus}
                  onChange={(value) => setFilters((current) => ({ ...current, paymentStatus: value }))}
                  options={["All", ...lookupSets.paymentStatuses]}
                />
              </FilterField>
              <div className="flex items-end">
                <button
                  type="button"
                  onClick={() =>
                    setFilters({
                      query: "",
                      dateFrom: "",
                      dateTo: "",
                      leadSource: "All",
                      serviceInterest: "All",
                      eventType: "All",
                      paymentStatus: "All",
                    })
                  }
                  className="focus-ring inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-lg border border-[#FF6A2B]/24 px-4 text-sm font-extrabold text-[#fff9ef]"
                  title="Clear all filters and show every journey"
                >
                  <SlidersHorizontal className="size-4" aria-hidden="true" />
                  Reset Filters
                </button>
              </div>
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-3 text-xs font-black uppercase tracking-[0.14em] text-[#FF6A2B]">
              <span className="inline-flex items-center gap-2 rounded-full border border-[#FF6A2B]/24 bg-white/5 px-3 py-2">
                <Clock3 className="size-4" />
                Data source: admin journey endpoint, lead history, proposal, agreement, and payment records
              </span>
            </div>

            {error ? <Alert message={error} /> : null}
            {loading ? <EmptyState title="Loading sales dashboard" message="Checking proposal, agreement, payment, and timeline records." /> : null}

            {!loading && !error ? (
              <div className="mt-6 grid gap-8">
                <section className="grid gap-4">
                  <SectionHeading
                    icon={BarChart3}
                    title="Sales Pipeline Counts"
                    subtitle="How many leads and clients are currently in each stage."
                  />
                  <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                    {analytics.statusCounts.map((stage) => (
                      <PipelineStageCard
                        key={stage.label}
                        label={stage.label}
                        count={stage.count}
                        amount={stage.amount}
                        total={filteredJourneys.length}
                      />
                    ))}
                  </div>
                </section>

                <section className="grid gap-5 xl:grid-cols-[1.25fr_.95fr]">
                  <div className="command-center-card rounded-lg p-5">
                    <SectionHeading
                      icon={Activity}
                      title="Prospect-to-Client Funnel"
                      subtitle="Conversion from inquiry through paid client."
                    />
                    <div className="mt-5 grid gap-4">
                      {analytics.funnel.map((step, index) => (
                        <div key={step.label} className="rounded-lg border border-[#FF6A2B]/20 bg-white/5 p-4">
                          <div className="flex flex-wrap items-end justify-between gap-3">
                            <div>
                              <p className="text-sm font-black uppercase tracking-[0.12em] text-[#FF6A2B]">{step.label}</p>
                              <p className="mt-1 font-display text-2xl font-bold text-[#fff9ef]">{step.count}</p>
                            </div>
                            <div className="text-right text-xs font-black uppercase tracking-[0.12em] text-[#fff9ef]/64">
                              {index === 0 ? "Top of funnel" : `${formatPercent(step.conversionRate)} from prior stage`}
                            </div>
                          </div>
                          <div className="mt-3 h-3 rounded-full bg-white/10">
                            <div className="h-3 rounded-full bg-[#FF6A2B]" style={{ width: `${step.widthPercent}%` }} />
                          </div>
                          {index > 0 ? (
                            <p className="mt-2 text-sm font-semibold text-[#fff9ef]/70">
                              Drop-off: {step.dropOff} {step.dropOff === 1 ? "lead" : "leads"}
                            </p>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="grid gap-5">
                    <div className="command-center-card rounded-lg p-5">
                      <SectionHeading
                        icon={TrendingDown}
                        title="Drop-Off Points"
                        subtitle="Where prospects slow down or lose momentum."
                      />
                      <div className="mt-5 grid gap-3">
                        {analytics.dropOffs.slice(0, 6).map((dropOff) => (
                          <div key={dropOff.label} className="rounded-lg border border-[#FF6A2B]/20 bg-white/5 p-4">
                            <div className="flex items-center justify-between gap-3">
                              <p className="text-sm font-black uppercase tracking-[0.12em] text-[#FF6A2B]">{dropOff.label}</p>
                              <p className="text-sm font-bold text-[#fff9ef]">{dropOff.dropOff} lost</p>
                            </div>
                            <p className="mt-2 text-sm font-semibold leading-6 text-[#fff9ef]/70">
                              Conversion from previous stage: {formatPercent(dropOff.conversionRate)}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="command-center-card rounded-lg p-5">
                      <SectionHeading
                        icon={CalendarDays}
                        title="Lead Source Mix"
                        subtitle="How prospects heard about Kreatethedate."
                      />
                      <div className="mt-5 grid gap-3 sm:grid-cols-3">
                        {analytics.sourceInsights.slice(0, 3).map((insight) => (
                          <div key={insight.label} className="rounded-lg border border-[#FF6A2B]/20 bg-white/5 p-4">
                            <p className="text-xs font-black uppercase tracking-[0.12em] text-[#FF6A2B]">{insight.label}</p>
                            <p className="mt-2 font-display text-2xl font-bold text-[#fff9ef]">{insight.value}</p>
                            <p className="mt-3 text-sm font-semibold leading-6 text-[#fff9ef]/70">{insight.note}</p>
                          </div>
                        ))}
                      </div>
                      <div className="mt-5 grid gap-3">
                        {analytics.leadSources.slice(0, 8).map((source) => (
                          <BarRow
                            key={source.label}
                            label={source.label}
                            count={source.count}
                            converted={source.converted}
                            revenue={source.revenue}
                            conversionRate={source.conversionRate}
                            total={analytics.maxSourceCount}
                            showDetails
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </section>

                <section className="grid gap-5 xl:grid-cols-[1.05fr_.95fr]">
                  <div className="command-center-card rounded-lg p-5">
                    <SectionHeading
                      icon={BarChart3}
                      title="Status Chart"
                      subtitle="Number of leads in each pipeline stage."
                    />
                    <div className="mt-5 max-h-[560px] overflow-auto pr-1">
                      <div className="grid gap-3">
                        {analytics.statusCounts.map((status) => (
                          <BarRow
                            key={status.label}
                            label={status.label}
                            count={status.count}
                            revenue={status.amount}
                            total={analytics.maxStatusCount}
                            showDetails={false}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="grid gap-5">
                    <div className="command-center-card rounded-lg p-5">
                      <SectionHeading
                        icon={Clock3}
                        title="Average Time in Stage"
                        subtitle="Average time spent in each status when history data exists."
                      />
                      <div className="mt-5 grid max-h-[540px] gap-3 overflow-auto pr-1">
                        {analytics.averageDurations.map((item) => (
                          <div key={item.label} className="rounded-lg border border-[#FF6A2B]/20 bg-white/5 p-4">
                            <div className="flex items-center justify-between gap-3">
                              <p className="text-sm font-black uppercase tracking-[0.12em] text-[#FF6A2B]">{item.label}</p>
                              <p className="text-sm font-bold text-[#fff9ef]">{item.value}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="command-center-card rounded-lg p-5">
                      <SectionHeading
                        icon={AlertTriangle}
                        title="Stuck Leads"
                        subtitle="Clients sitting in a status longer than expected."
                      />
                      <div className="mt-5 grid gap-3">
                        {stuckLeads.length ? (
                          stuckLeads.map(({ journey, currentStatus, daysInStage, threshold }) => (
                            <article key={journey.leadId} className="rounded-lg border border-[#FF6A2B]/20 bg-white/5 p-4">
                              <div className="flex items-start justify-between gap-3">
                                <div>
                                  <p className="font-display text-xl font-bold text-[#fff9ef]">{journey.leadName}</p>
                                  <p className="mt-1 text-sm font-semibold text-[#fff9ef]/72">{journey.leadEmail}</p>
                                </div>
                                <span className="rounded-full border border-red-400/30 bg-red-500/10 px-3 py-1 text-xs font-black uppercase tracking-[0.12em] text-red-100">
                                  {daysInStage} days
                                </span>
                              </div>
                              <p className="mt-3 text-sm font-bold text-[#fff9ef]">{currentStatus}</p>
                              <p className="mt-1 text-sm font-semibold text-[#fff9ef]/70">
                                Expected by day {threshold}, currently {daysInStage - threshold} day
                                {daysInStage - threshold === 1 ? "" : "s"} over target.
                              </p>
                            </article>
                          ))
                        ) : (
                          <EmptyState title="No stuck leads" message="Everything in the current filtered view looks on pace." />
                        )}
                      </div>
                    </div>
                  </div>
                </section>

                <section className="command-center-card rounded-lg p-5">
                  <SectionHeading
                    icon={Users}
                    title="Newest Leads Needing Attention"
                    subtitle="Recent prospects that still need guided follow-up."
                  />
                  <div className="mt-5 grid gap-3">
                    {attentionLeads.length ? (
                      attentionLeads.map((journey) => (
                        <article
                          key={journey.leadId}
                          className="grid gap-4 rounded-lg border border-[#FF6A2B]/20 bg-white/5 p-4 lg:grid-cols-[1.2fr_.8fr_.8fr_auto] lg:items-center"
                        >
                          <div>
                            <div className="flex flex-wrap items-center gap-2">
                              <h3 className="font-display text-2xl font-bold text-[#fff9ef]">{journey.leadName}</h3>
                              <span className="rounded-full bg-[#FF6A2B] px-3 py-1 text-xs font-black uppercase tracking-[0.12em] text-[#050505]">
                                {journey.currentStatus}
                              </span>
                            </div>
                            <p className="mt-2 text-sm font-semibold leading-6 text-[#fff9ef]/72">
                              {journey.leadEmail} {journey.leadSource ? `| ${journey.leadSource}` : ""}
                            </p>
                          </div>
                          <div className="text-sm font-bold leading-6 text-[#fff9ef]">
                            <p>Service: {journey.serviceInterest || "Not set"}</p>
                            <p className="mt-1 text-[#fff9ef]/72">Event: {journey.eventType || "Not set"}</p>
                          </div>
                          <div className="text-sm font-bold leading-6 text-[#fff9ef]">
                            <p>Payment: {journey.paymentStatus}</p>
                            <p className="mt-1 text-[#fff9ef]/72">Next: {journey.nextStep}</p>
                          </div>
                          <Link
                            to={`/admin/leads/${journey.leadId}`}
                            className="focus-ring inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-[#FF6A2B] px-4 text-sm font-extrabold text-[#050505]"
                            title={`Open the timeline for ${journey.leadName}`}
                          >
                            Open Timeline
                            <ArrowRight className="size-4" aria-hidden="true" />
                          </Link>
                        </article>
                      ))
                    ) : (
                      <EmptyState
                        title="No journey records yet"
                        message="Your first lead journey will appear here once a new inquiry enters the pipeline."
                      />
                    )}
                  </div>
                </section>

                <section className="command-center-card rounded-lg p-5 sm:p-6">
                  <SectionHeading
                    icon={LayoutDashboard}
                    title="Stage Pipeline Rows"
                    subtitle="Every client journey status currently in the dashboard."
                  />
                  <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                    {pipelineStages.map((stage) => (
                      <StatusCountCard
                        key={stage}
                        label={stage}
                        count={countByStatuses(filteredJourneys, stage === "New Inquiry" ? ["New Inquiry", "Inquiry Submitted"] : [stage])}
                        total={filteredJourneys.length}
                      />
                    ))}
                  </div>
                </section>
              </div>
            ) : null}
          </section>
        </div>
      </section>
    </main>
  );
}

function buildAnalytics(journeys: JourneySummary[]) {
  const statusCounts = pipelineStages.map((stage) => ({
    label: stage,
    count: countByStatuses(journeys, stage === "New Inquiry" ? ["New Inquiry", "Inquiry Submitted"] : [stage]),
    amount: journeys
      .filter((journey) => canonicalStatus(journey.currentStatus) === stage || (stage === "New Inquiry" && canonicalStatus(journey.currentStatus) === "Inquiry Submitted"))
      .reduce((sum, journey) => sum + journey.amountDueCents, 0),
  }));

  const maxStatusCount = Math.max(1, ...statusCounts.map((item) => item.count));

  const funnel = funnelStages.map((stage, index) => {
    const count = countByStatuses(journeys, stage.statuses);
    const previousCount = index === 0 ? journeys.length : countByStatuses(journeys, funnelStages[index - 1].statuses);
    return {
      label: stage.label,
      count,
      conversionRate: previousCount ? count / previousCount : 0,
      dropOff: Math.max(0, previousCount - count),
      widthPercent: journeys.length ? Math.max(18, Math.round((count / journeys.length) * 100)) : 18,
    };
  });

  const sourceCountsMap = new Map<string, number>();
  const sourceRevenueMap = new Map<string, number>();
  const sourceConvertedMap = new Map<string, number>();
  for (const journey of journeys) {
    const source = journey.leadSource || "Unknown";
    sourceCountsMap.set(source, (sourceCountsMap.get(source) || 0) + 1);
    sourceRevenueMap.set(source, (sourceRevenueMap.get(source) || 0) + journey.amountPaidCents);
    if (canonicalStatus(journey.currentStatus) === "Paid In Full") {
      sourceConvertedMap.set(source, (sourceConvertedMap.get(source) || 0) + 1);
    }
  }
  const leadSources = [...sourceCountsMap.entries()]
    .map(([label, count]) => ({
      label,
      count,
      converted: sourceConvertedMap.get(label) || 0,
      revenue: sourceRevenueMap.get(label) || 0,
      conversionRate: count ? (sourceConvertedMap.get(label) || 0) / count : 0,
    }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  const maxSourceCount = Math.max(1, ...leadSources.map((item) => item.count));

  const durationMap = new Map<string, number[]>();
  for (const journey of journeys) {
    const history = [...journey.statusHistory].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    if (!history.length) continue;
    for (let index = 0; index < history.length; index += 1) {
      const current = history[index];
      const currentStatus = canonicalStatus(current.status);
      const start = new Date(current.createdAt).getTime();
      const end = index + 1 < history.length ? new Date(history[index + 1].createdAt).getTime() : Date.now();
      const duration = Math.max(0, end - start);
      if (!Number.isFinite(duration) || duration === 0) continue;
      const bucket = durationMap.get(currentStatus) || [];
      bucket.push(duration);
      durationMap.set(currentStatus, bucket);
    }
  }

  const averageDurations = pipelineStages
    .map((stage) => {
      const durations = durationMap.get(stage) || [];
      return {
        label: stage,
        value: durations.length ? `${formatDays(durations.reduce((sum, value) => sum + value, 0) / durations.length / 86400000)} avg` : "Awaiting history",
      };
    });

  const dropOffs = funnel.slice(1).map((stage, index) => {
    const previous = funnel[index];
    return {
      label: `${previous.label} -> ${stage.label}`,
      dropOff: stage.dropOff,
      conversionRate: stage.conversionRate,
    };
  });

  const topSource = leadSources[0];
  const topConversionSource = [...leadSources].sort((a, b) => b.conversionRate - a.conversionRate || b.count - a.count || a.label.localeCompare(b.label))[0];
  const topRevenueSource = [...leadSources].sort((a, b) => b.revenue - a.revenue || b.count - a.count || a.label.localeCompare(b.label))[0];

  const sourceInsights = [
    {
      label: "Top Source",
      value: topSource?.label || "Awaiting data",
      note: topSource ? `${topSource.count} leads · ${money(topSource.revenue)} revenue` : "No lead source data available yet.",
    },
    {
      label: "Top Conversion",
      value: topConversionSource ? `${Math.round(topConversionSource.conversionRate * 100)}%` : "Awaiting data",
      note: topConversionSource ? `${topConversionSource.converted} paid clients from ${topConversionSource.label}` : "No conversion history available yet.",
    },
    {
      label: "Top Revenue",
      value: topRevenueSource ? money(topRevenueSource.revenue) : money(0),
      note: topRevenueSource ? `Revenue attributed to ${topRevenueSource.label}` : "No source revenue has been attributed yet.",
    },
  ];

  return {
    statusCounts,
    funnel,
    leadSources,
    sourceInsights,
    maxStatusCount,
    maxSourceCount,
    averageDurations,
    dropOffs,
  };
}

function countByStatuses(journeys: JourneySummary[], statuses: readonly string[]) {
  const normalized = new Set(statuses.map((status) => canonicalStatus(status)));
  return journeys.filter((journey) => normalized.has(canonicalStatus(journey.currentStatus))).length;
}

function statusStartedAt(journey: JourneySummary, status: string) {
  const history = [...journey.statusHistory].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  const match = [...history].reverse().find((event) => canonicalStatus(event.status) === status);
  return match?.createdAt || journey.leadCreatedAt;
}

function daysSince(value: string) {
  const parsed = new Date(value).getTime();
  if (!Number.isFinite(parsed)) return 0;
  return Math.max(0, Math.floor((Date.now() - parsed) / 86400000));
}

function canonicalStatus(status: string) {
  if (status === "Inquiry Submitted") return "New Inquiry";
  return status;
}

function includesText(value: string, query: string) {
  return value.toLowerCase().includes(query.toLowerCase());
}

function uniqueSorted(values: string[]) {
  return [...new Set(values.filter(Boolean))].sort((a, b) => a.localeCompare(b));
}

function splitValues(values: string[]) {
  return values.flatMap((value) =>
    String(value || "")
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean),
  );
}

function formatPercent(value: number) {
  return `${Math.round(value * 100)}%`;
}

function formatDays(value: number) {
  if (!Number.isFinite(value)) return "0 days";
  const rounded = Math.round(value * 10) / 10;
  return `${rounded} day${rounded === 1 ? "" : "s"}`;
}

function MetricCard({ icon: Icon, label, value }: { icon: typeof Users; label: string; value: number }) {
  const description =
    label === "Total Leads"
      ? "All filtered prospects and clients in the current view."
      : label === "Proposal Stage"
        ? "Prospects with an active proposal or proposal-related status."
        : label === "Agreement Stage"
          ? "Clients who have reached agreement review or signature."
          : "Clients with invoices, deposits, or payments currently in motion.";
  return (
    <article className="command-center-card rounded-lg p-4" title={description}>
      <Icon className="size-5 text-[#FF6A2B]" aria-hidden="true" />
      <p className="mt-4 font-display text-3xl font-bold text-[#fff9ef]">{value}</p>
      <p className="mt-1 text-xs font-black uppercase tracking-[0.12em] text-[#fff9ef]/66">{label}</p>
      <p className="mt-3 text-sm font-semibold leading-6 text-[#fff9ef]/72">{description}</p>
    </article>
  );
}

function StatusCountCard({ label, count, total }: { label: string; count: number; total: number }) {
  const percent = total ? Math.round((count / total) * 100) : 0;
  return (
    <article className="rounded-lg border border-[#FF6A2B]/20 bg-white/5 p-4" title={`${label} status count`}>
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm font-black uppercase tracking-[0.12em] text-[#FF6A2B]">{label}</p>
        <span className="rounded-full bg-[#FF6A2B] px-3 py-1 text-xs font-black text-[#050505]">{count}</span>
      </div>
      <p className="mt-2 text-sm font-semibold leading-6 text-[#fff9ef]/70">{count} lead{count === 1 ? "" : "s"} currently in this stage.</p>
      <div className="mt-3 h-2 rounded-full bg-white/10">
        <div className="h-2 rounded-full bg-[#FF6A2B]" style={{ width: `${percent}%` }} />
      </div>
      <p className="mt-2 text-xs font-semibold text-[#fff9ef]/66">{percent}% of filtered leads</p>
    </article>
  );
}

function PipelineStageCard({ label, count, amount, total }: { label: string; count: number; amount: number; total: number }) {
  const percent = total ? Math.round((count / total) * 100) : 0;
  return (
    <article className="rounded-lg border border-[#FF6A2B]/20 bg-white/5 p-4" title={`${label} pipeline stage`}>
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm font-black uppercase tracking-[0.12em] text-[#FF6A2B]">{label}</p>
        <span className="rounded-full bg-[#FF6A2B] px-3 py-1 text-xs font-black text-[#050505]">{count}</span>
      </div>
      <p className="mt-2 text-sm font-semibold leading-6 text-[#fff9ef]/70">
        {count} lead{count === 1 ? "" : "s"} in this stage and {percent}% of the filtered journey set.
      </p>
      <p className="mt-2 text-sm font-semibold text-[#fff9ef]/72">{money(amount)} in total value at this stage.</p>
      <div className="mt-3 h-2 rounded-full bg-white/10">
        <div className="h-2 rounded-full bg-[#FF6A2B]" style={{ width: `${Math.max(8, percent)}%` }} />
      </div>
      <p className="mt-2 text-xs font-semibold text-[#fff9ef]/66">{percent}% of filtered leads</p>
    </article>
  );
}

function BarRow({
  label,
  count,
  converted = 0,
  conversionRate = 0,
  revenue = 0,
  showDetails = false,
  total,
}: {
  label: string;
  count: number;
  converted?: number;
  conversionRate?: number;
  revenue?: number;
  showDetails?: boolean;
  total: number;
}) {
  const percent = total ? Math.max(6, Math.round((count / total) * 100)) : 0;
  return (
    <div className="grid gap-2" title={`${label} summary`}>
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-semibold text-[#fff9ef]">{label}</p>
        <div className="flex flex-wrap items-center gap-2 text-xs font-black uppercase tracking-[0.12em] text-[#FF6A2B]">
          <span>{count}</span>
          {showDetails ? <span className="rounded-full border border-[#FF6A2B]/24 px-2 py-1 text-[#fff9ef]/75">{Math.round(conversionRate * 100)}%</span> : null}
          {showDetails ? <span className="rounded-full border border-[#FF6A2B]/24 px-2 py-1 text-[#fff9ef]/75">{money(revenue)}</span> : null}
        </div>
      </div>
      <div className="h-3 rounded-full bg-white/10">
        <div className="h-3 rounded-full bg-[#FF6A2B]" style={{ width: `${Math.min(100, percent)}%` }} />
      </div>
      {showDetails ? (
        <p className="text-xs font-semibold text-[#fff9ef]/66">
          Represents {percent}% of the filtered journey set and {converted} paid client{converted === 1 ? "" : "s"} from this source.
        </p>
      ) : (
        <p className="text-xs font-semibold text-[#fff9ef]/66">Represents {percent}% of the filtered journey set.</p>
      )}
    </div>
  );
}

function money(cents: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format((Number(cents) || 0) / 100);
}

function SectionHeading({ icon: Icon, title, subtitle }: { icon: typeof LayoutDashboard; title: string; subtitle: string }) {
  return (
    <div className="border-b border-[#FF6A2B]/14 pb-4">
      <p className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.18em] text-[#FF6A2B]">
        <Icon className="size-4" aria-hidden="true" />
        Sales Dashboard
      </p>
      <h2 className="mt-1 font-display text-3xl font-bold text-[#fff9ef]">{title}</h2>
      <p className="mt-2 text-sm font-semibold leading-7 text-[#fff9ef]/72">{subtitle}</p>
    </div>
  );
}

function FilterField({ children, label }: { children: ReactNode; label: string }) {
  return (
    <label className="grid gap-2 text-sm font-bold text-[#fff9ef]">
      {label}
      {children}
    </label>
  );
}

function SelectField({
  options,
  value,
  onChange,
}: {
  options: string[];
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="command-center-field focus-ring min-h-11 rounded-lg px-4 text-sm font-semibold"
    >
      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
  );
}

function Alert({ message }: { message: string }) {
  return <p className="mt-5 rounded-lg border border-red-400/30 bg-red-500/10 px-5 py-4 text-sm font-bold text-red-100">{message}</p>;
}

function EmptyState({ message, title }: { message: string; title: string }) {
  return (
    <div className="mt-5 rounded-lg border border-[#FF6A2B]/20 bg-white/5 p-6 text-center">
      <p className="font-display text-2xl font-bold text-[#fff9ef]">{title}</p>
      <p className="mt-2 text-sm font-semibold text-[#fff9ef]/72">{message}</p>
    </div>
  );
}
